import React from 'react';
import styles from './HomePage.module.scss';
import { useCallback, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { MAX_URL } from '../../data/config';
import { ROUTES } from '../../data/navigation';
import type { Project } from '../../types/projectsTable';
import { useAppSelector } from '../../app/hooks';
import { selectOrganizationId } from '../../entities/organization/model/slice';
import { useGetProjectsQuery } from '../../entities/project/api/projectApi';
import { useGetLatestNewsQuery } from '../../entities/news/api/newsApi';
import { downloadFile } from '../../shared/api/download';
import { notifyError, notifyInfo } from '../../shared/lib/notifications';

import { ActionPanel } from '../../components/ActionPanel/ActionPanel';
import { ProjectsPanel } from '../../components/ProjectsPanel/ProjectsPanel';
import { GuideCard } from '../../components/GuideCard/GuideCard';
import { VideoCard } from '../../components/VideoCard/VideoCard';
import { MaxCard } from '../../components/MaxCard/MaxCard';
import { NewsPanel } from '../../components/NewsPanel/NewsPanel';
import { MaxQrModal } from '../../components/MaxQrModal/MaxQrModal';
import type { NewsArticle } from '../../entities/news/model/types';

const RECENT_PROJECTS_LIMIT = 7;

const LATEST_NEWS_LIMIT = 5;

export const HomePage: FC = () => {
    const navigate = useNavigate();
    const organizationId = useAppSelector(selectOrganizationId);

    const [qrOpen, setQrOpen] = useState(false);

    const { data: allProjects = [], isLoading: isProjectsLoading } = useGetProjectsQuery(
        { organizationId },
        { skip: !organizationId },
    );

    const isProjectsPending = isProjectsLoading || !organizationId;

    const { data: newsResponse, isLoading: isNewsLoading } = useGetLatestNewsQuery({ page_size: LATEST_NEWS_LIMIT });
    const news = newsResponse?.data ?? [];

    const recentProjects = useMemo(
        () =>
            [...allProjects]
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .slice(0, RECENT_PROJECTS_LIMIT),
        [allProjects],
    );

    const lastProject = useMemo(() => {
        const byDate = [...allProjects].sort((a, b) => b.date.localeCompare(a.date));
        const newest = byDate.find((project) => project.status !== 'done') ?? byDate[0];
        if (!newest) return null;

        return {
            id: newest.id,
            name: newest.name,
            done: newest.done,
            total: newest.total,
            price: newest.price,
        };
    }, [allProjects]);

    const closeQr = useCallback(() => setQrOpen(false), []);

    const openProject = (project: Project): void => {
        navigate(`/projects/${project.id}`);
    };

    const openMax = (): void => {
        window.open(MAX_URL, '_blank', 'noopener,noreferrer');
    };

    const downloadGuide = async (): Promise<void> => {
        try {
            await downloadFile('/api/organizations/user_guide', 'Руководство пользователя.pdf');
        } catch (error) {
            notifyError(
                error instanceof Error ? error.message : 'Не удалось скачать руководство',
            );
        }
    };

    return (
        <div className={styles.dashboard}>
            <div className={styles.left}>
                <ActionPanel
                    lastProject={lastProject}
                    isLoading={isProjectsPending}
                    onCreate={() => navigate(ROUTES.projects, { state: { openNew: true } })}
                    onOpenProjects={() => navigate(ROUTES.projects)}
                    onOpenProject={(id) => navigate(`/projects/${id}`)}
                />

                <ProjectsPanel
                    projects={recentProjects}
                    isLoading={isProjectsPending}
                    onOpenSection={() => navigate(ROUTES.projects)}
                    onProjectMenu={openProject}
                />
            </div>

            <div className={styles.right}>
                <div className={styles.pair}>
                    <GuideCard compact onDownload={downloadGuide} />
                    <VideoCard compact />
                </div>

                <MaxCard onOpenMax={openMax} onOpenQr={() => setQrOpen(true)} />

                <NewsPanel
                    news={news}
                    isLoading={isNewsLoading}
                    onOpenAll={() => navigate('/news')}
                    onOpenItem={(item) => navigate(`/news/${item.id}`, { state: { article: item } })}
                />

            </div>

            <MaxQrModal open={qrOpen} onClose={closeQr} />
        </div>
    );
};